import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "../../../axios";
import usePageTitle from "../../pageTitle";

function DisbursementPage() {
  usePageTitle("Claim Prize");

  const { ref } = useParams();
  const navigate = useNavigate();

  const submittingRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [spinReference, setSpinReference] = useState("");
  const [segment, setSegment] = useState(null);
  const [fields, setFields] = useState([]);
  const [destination, setDestination] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadDisbursement = useCallback(async () => {
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await axios.get(
        `web/tm/spin/wheel/spin/${ref}/disburse/details.json`,
      );

      if (response.data.status !== "success") {
        setError(
          response.data.data?.message ||
            "Unable to load the prize claim details.",
        );
        return;
      }

      const data = response.data.data;
      const segmentData = data.segment;
      const disbursementFields = Array.isArray(data.disbursement)
        ? data.disbursement
        : [];

      setSegment(segmentData);
      setSpinReference(segmentData?.spin_reference || "");
      setFields(disbursementFields);

      setDestination(
        Object.fromEntries(disbursementFields.map((field) => [field.key, ""])),
      );

      if (!segmentData?.spin_reference || !disbursementFields.length) {
        setError(
          "The prize claim details are incomplete. Please contact the promotion organiser.",
        );
      }
    } catch (requestError) {
      setError(
        requestError.response?.data?.data?.message ||
          "Unable to load the prize claim details. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }, [ref]);

  useEffect(() => {
    loadDisbursement();
  }, [loadDisbursement]);

  const handleFieldChange = (key, value) => {
    setDestination((previous) => ({
      ...previous,
      [key]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event?.preventDefault();

    // A ref prevents duplicate requests before React updates the state.
    if (submittingRef.current || !spinReference || success) {
      return;
    }

    const missingField = fields.find(
      (field) =>
        field.required !== false &&
        !String(destination[field.key] ?? "").trim(),
    );

    if (missingField) {
      setError(`${missingField.label} is required.`);
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    setError("");

    try {
      const response = await axios.post(
        "web/tm/spin/wheel/spin/disburse.json",
        {
          spin: spinReference,
          destination,
        },
      );

      if (response.data.status !== "success") {
        setError(
          response.data.data?.message ||
            "Unable to claim your prize. Please try again.",
        );
        return;
      }

      setSuccess(
        response.data.data?.message ||
          "Your prize has been claimed successfully.",
      );
    } catch (requestError) {
      setError(
        requestError.response?.data?.data?.message ||
          "Unable to claim your prize. Please try again.",
      );
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "70vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography color="text.secondary">
          Loading prize claim details...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#F7F8FA",
        py: { xs: 3, md: 7 },
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, sm: 4 },
            border: "1px solid #E5E7EB",
            borderRadius: 2,
            bgcolor: "#FFFFFF",
          }}
        >
          <Stack spacing={3}>
            <Box>
              <Box mb={2}>
                <Typography
                  variant="overline"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 700,
                    letterSpacing: 1.2,
                  }}
                  mb={5}
                >
                  SPIN & WIN: PRIZE REDEMPTION
                </Typography>
              </Box>

              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  mt: 0.5,
                  color: "#111827",
                }}
              >
                Claim Your Prize
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {segment?.title
                  ? `Congratulations! You won ${segment.title}. Enter your details below to receive your prize.`
                  : "Enter your details below to receive your prize."}
              </Typography>
            </Box>

            {error && (
              <Box mb={5}>
                <Alert severity="error" onClose={() => setError("")}>
                  {error}
                </Alert>
              </Box>
            )}

            {success && <Alert severity="success">{success}</Alert>}

            {!success && fields.length > 0 && (
              <Box component="form" onSubmit={handleSubmit}>
                <Stack spacing={3}>
                  {fields.map((field) => {
                    const hasOptions =
                      Array.isArray(field.options) && field.options.length > 0;

                    return (
                      <TextField
                        key={field.key}
                        select={hasOptions}
                        fullWidth
                        label={field.label}
                        name={field.key}
                        value={destination[field.key] ?? ""}
                        onChange={(event) =>
                          handleFieldChange(field.key, event.target.value)
                        }
                        required={field.required !== false}
                        disabled={submitting}
                        type={
                          field.type ||
                          (field.key === "account_number" ||
                          field.key === "phone_number"
                            ? "tel"
                            : "text")
                        }
                        autoComplete={
                          field.key === "account_name"
                            ? "name"
                            : field.key === "phone_number"
                              ? "tel"
                              : "off"
                        }
                      >
                        {hasOptions &&
                          field.options.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                      </TextField>
                    );
                  })}

                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={submitting || !spinReference || Boolean(success)}
                    startIcon={
                      submitting ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : undefined
                    }
                    sx={{
                      mt: 1,
                      minHeight: 48,
                      fontWeight: 700,
                      textTransform: "none",
                    }}
                  >
                    {submitting ? "Claiming Prize..." : "Claim Prize"}
                  </Button>

                  <Button
                    variant="text"
                    color="inherit"
                    disabled={submitting}
                    onClick={() => navigate(-1)}
                    sx={{
                      textTransform: "none",
                    }}
                  >
                    Back
                  </Button>
                </Stack>
              </Box>
            )}

            {!success && fields.length === 0 && !error && (
              <Alert severity="warning">
                No prize claim fields are available.
              </Alert>
            )}

            {success && (
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={() => navigate(-1)}
                sx={{
                  minHeight: 48,
                  fontWeight: 700,
                  textTransform: "none",
                }}
              >
                Back to Wheel
              </Button>
            )}
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}

export default DisbursementPage;
